import { useEffect, useState } from "react";
import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  Cable,
  FileText,
  Globe,
  LayoutDashboard,
  Network,
  PhoneCall,
  ShieldAlert,
  User as UserIcon,
  GitBranch,
} from "lucide-react";
import { api } from "@/lib/api";
import { KPICard, PageHeader, StatusPill } from "@/components/uiext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useEngineAliveDebounced } from "@/lib/useEngineAlive";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const SEC_LABEL = {
  auth_fail: "Auth failures",
  domain_reject: "Domain rejects",
  domain_mismatch: "Domain mismatches",
  ip_auth_fail: "IP auth fail",
  quota_block: "Quota blocks",
  cps_exceeded: "CPS exceeded",
  acl_block: "ACL blocks",
  ip_locked: "IPs locked",
};

// Phase 6r — Trial / billing banner. Stays quiet for paid + unlimited
// states; renders a single coloured row otherwise so the operator
// always knows where they stand without surprise call-blocking.
function TrialBanner({ billing }) {
  const { t } = useTranslation();
  if (!billing) return null;
  const { state, days_left: daysLeft, trial_expires_at: expiresAt, exempt_until: exemptUntil } = billing;

  // Only surface states that need attention.
  const showSoon = state === "trial_active" && typeof daysLeft === "number" && daysLeft <= 7;
  const showExpired = state === "trial_expired";
  const showExempt = state === "exempt";
  if (!showSoon && !showExpired && !showExempt) return null;

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return iso;
    }
  };

  let cls, Icon, title, body;
  if (showExpired) {
    cls = "border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]";
    Icon = ShieldAlert;
    title = t("dashboard.trialBanner.expiredTitle", "Free trial expired");
    body = t(
      "dashboard.trialBanner.expiredBody",
      "Calls are currently rejected by the engine until you upgrade or contact your platform administrator. Login still works so you can finish setup.",
    );
  } else if (showSoon) {
    cls = "border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]";
    Icon = AlertTriangle;
    title = t("dashboard.trialBanner.endingTitle", "Trial ending soon");
    body = t("dashboard.trialBanner.endingBody", "Your free trial ends on {{date}} ({{days}} day(s) left). Upgrade to keep your calls flowing.", { date: fmt(expiresAt), days: daysLeft });
  } else {
    // exempt
    cls = "border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]";
    Icon = BadgeCheck;
    title = t("dashboard.trialBanner.exemptTitle", "Billing exempt");
    body = exemptUntil
      ? t("dashboard.trialBanner.exemptBodyUntil", "Your account is exempt from billing until {{date}}.", { date: fmt(exemptUntil) })
      : t("dashboard.trialBanner.exemptBody", "Your account is exempt from billing until your platform administrator changes that. Calls are not gated by trial expiry.");
  }
  return (
    <div
      className={`flex items-start gap-3 rounded-md border px-4 py-3 mb-4 ${cls}`}
      data-testid={`trial-banner-${state}`}
      role="status"
    >
      <Icon className="h-5 w-5 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs opacity-90 mt-0.5">{body}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  // Phase 6r — trial / billing-exempt status banner.
  const [billing, setBilling] = useState(null);
  // Phase 6ak (Hotfix Z.4) — debounce engine_alive flicker. Dashboard
  // polls /dashboard/stats every 5s; 3 consecutive misses = ~15s
  // before flipping the chip to DOWN. Until then, show "Checking…".
  // Pass `stats` as `tick` so the hook re-runs even when the primitive
  // engine_alive value is stuck-false between polls.
  const engineState = useEngineAliveDebounced(stats?.engine_alive, 3, stats);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api
        .get("/dashboard/stats")
        .then((r) => alive && setStats(r.data))
        .catch(() => {});
      api
        .get("/security/events?limit=8")
        .then((r) => alive && setEvents(r.data))
        .catch(() => {});
      api
        .get("/billing/trial-status")
        .then((r) => alive && setBilling(r.data))
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  return (
    <div data-testid="page-dashboard">
      <PageHeader
        title={t("dashboard.title")}
        subtitle={t("pages.security.subtitle")}
        icon={LayoutDashboard}
        testid="page-header-dashboard"
      />

      {/* Phase 6r — Trial / billing-exempt banner. Only renders when
          there's something for the operator to act on (trial expiring
          soon, expired, or exempt). Paid + unlimited stay quiet. */}
      <TrialBanner billing={billing} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <KPICard
          testid="kpi-active-calls"
          label={t("dashboard.kpi.activeCalls")}
          value={stats?.active_calls ?? "—"}
          hint="right now"
          icon={PhoneCall}
          accent="accent"
        />
        <KPICard
          testid="kpi-cdrs"
          label={t("dashboard.kpi.last24h")}
          value={stats?.cdrs_24h ?? "—"}
          hint={
            stats?.asr_24h != null ? `ASR ${stats.asr_24h}% • avg ${stats.avg_duration_s}s` : null
          }
          icon={FileText}
        />
        <KPICard
          testid="kpi-tenants"
          label="Users"
          value={stats ? `${stats.customers || 0}` : "—"}
          hint="SIP endpoints"
          icon={Network}
        />
        <KPICard
          testid="kpi-infra"
          label="Trunks • Domains"
          value={stats ? `${stats.trunks} • ${stats.domains}` : "—"}
          hint="active infra"
          icon={Cable}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-4 mt-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <span className="text-base font-semibold">Call volume (last 24h)</span>
              <StatusPill
                kind={
                  engineState === "up"
                    ? "ok"
                    : engineState === "down"
                    ? "danger"
                    : "warn"
                }
              >
                {engineState === "up"
                  ? t("dashboard.engine.up", "Engine UP")
                  : engineState === "down"
                  ? t("dashboard.engine.down", "Engine DOWN")
                  : t("dashboard.engine.checking", "Checking…")}
              </StatusPill>
            </CardTitle>
          </CardHeader>
          <CardContent className="h-[260px]" style={{minHeight: 260}}>
            <ResponsiveContainer width="100%" height={260} minWidth={0}>
              <BarChart
                data={stats?.calls_by_hour || []}
                margin={{ top: 8, right: 8, bottom: 0, left: -20 }}
              >
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="hour"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  cursor={{ fill: "hsl(var(--accent)/0.08)" }}
                />
                <Bar dataKey="count" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-[hsl(var(--security))]" /> Security (24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(SEC_LABEL).map(([k, l]) => (
                <div
                  key={k}
                  className="rounded-md border border-border bg-background/60 px-3 py-2 flex items-center justify-between"
                >
                  <div className="text-xs text-muted-foreground">{l}</div>
                  <div className="text-lg font-semibold tabular-nums">
                    {stats?.security_24h?.[k] ?? 0}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                {t("dashboard.latestEvents", "Latest events")}
              </div>
              <div className="space-y-1.5">
                {events.length === 0 && (
                  <div className="text-xs text-muted-foreground">
                    {t("dashboard.noRecentEvents", "No recent events — quiet night.")}
                  </div>
                )}
                {events.slice(0, 6).map((e, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-xs"
                    data-testid="recent-security-row"
                  >
                    <StatusPill kind="security">{e.type}</StatusPill>
                    <span className="font-mono text-foreground">{e.ip}</span>
                    <span className="text-muted-foreground ml-auto">
                      {new Date(e.ts).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
